import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Linkedin, Phone, Mail, MapPin } from "lucide-react";
import { services } from "@/data/services";

const washingtonCities = [
  { name: "Seattle", slug: "seattle" },
  { name: "Bellevue", slug: "bellevue" },
  { name: "Tacoma", slug: "tacoma" },
];

const arkansasCities = [
  { name: "Little Rock", slug: "little-rock" },
  { name: "Conway", slug: "conway" },
  { name: "Benton", slug: "benton" },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks: { icon: typeof Instagram; href: string; label: string }[] = [
    { icon: Instagram, href: "https://www.instagram.com/luminatemedia.wa/", label: "Instagram" },
    { icon: Facebook, href: "https://www.facebook.com/luminateremedia/", label: "Facebook" },
  ];

  return (
    <footer className="bg-secondary border-t border-border pb-20 md:pb-0">
      <div className="container mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <span className="font-serif text-2xl text-foreground">
                <span className="font-semibold">luminate</span><span className="font-normal text-primary">media</span>
              </span>
            </Link>
            <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
              Premium real estate photography, video, and drone services helping 
              agents and developers sell properties faster in Washington and Arkansas.
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="p-2 rounded-full bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-foreground mb-6">
              Services
            </h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.slug}>
                  <Link
                    to={`/services/${service.slug}`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations Column */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-foreground mb-6">
              Service Areas
            </h4>
            <ul className="space-y-3">
              <li className="text-xs text-primary font-medium uppercase tracking-wider mb-2">
                Washington
              </li>
              {washingtonCities.map((city) => (
                <li key={city.slug}>
                  <span className="text-sm text-muted-foreground">
                    {city.name}
                  </span>
                </li>
              ))}
              <li className="text-xs text-primary font-medium uppercase tracking-wider mb-2 mt-4">
                Arkansas
              </li>
              {arkansasCities.map((city) => (
                <li key={city.slug}>
                  <span className="text-sm text-muted-foreground">
                    {city.name}
                  </span>
                </li>
              ))}
              <li className="mt-4">
                <Link
                  to="/locations"
                  className="text-sm text-primary font-medium hover:text-primary/80 transition-colors"
                >
                  View All Locations →
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-foreground mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+13602008922"
                  className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <Phone size={16} className="text-primary" />
                  (360) 200-8922
                </a>
              </li>
              <li>
                <a
                  href="mailto:team@luminate.media"
                  className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <Mail size={16} className="text-primary" />
                  team@luminate.media
                </a>
              </li>
              <li>
                <span className="flex items-center gap-3 text-sm text-muted-foreground">
                  <MapPin size={16} className="text-primary" />
                  Washington & Arkansas
                </span>
              </li>
            </ul>
            <Link
              to="/book"
              className="inline-block mt-6 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              Book Now
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {currentYear} Luminate Media. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="https://listings.luminate.media/portal"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Client Portal
            </a>
            <Link
              to="/privacy"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
